import { useEffect, useRef, useState } from "react";
import { AlertCircle, Check, CheckCircle, ChevronDown } from "lucide-react";
import { useConversationAnnotations } from "@/hooks/conversation/useConversationAnnotations";

interface ConversationFollowUpPillProps {
  conversationId: string;
}

/**
 * Status pill in the page header showing follow-up state. Three states:
 *
 *   none         — no follow-up needed
 *   needs        — flagged with a reason
 *   followed_up  — closed out, timestamp captured
 *
 * Click opens a tiny inline popover for state + reason text. Persists
 * through useConversationAnnotations (localStorage phase 1).
 */
export function ConversationFollowUpPill({ conversationId }: ConversationFollowUpPillProps) {
  const { annotations, setFollowUp } = useConversationAnnotations(conversationId);
  const [open, setOpen] = useState(false);
  const [reasonDraft, setReasonDraft] = useState(annotations.followUpReason ?? "");
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setReasonDraft(annotations.followUpReason ?? "");
  }, [annotations.followUpReason]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const status = annotations.followUpStatus;

  const pillStyle: React.CSSProperties =
    status === "needs"
      ? {
          background: "linear-gradient(90deg, rgba(255,77,184,0.12), rgba(255,77,184,0.06))",
          borderColor: "rgba(255,77,184,0.5)",
          color: "#FF4DB8",
        }
      : status === "followed_up"
      ? {
          background: "linear-gradient(90deg, rgba(78,156,255,0.12), rgba(78,156,255,0.06))",
          borderColor: "rgba(78,156,255,0.5)",
          color: "#4E9CFF",
        }
      : {
          background: "#FFFFFF",
          borderColor: "rgba(78,156,255,0.3)",
          color: "#374151",
        };

  const label =
    status === "needs"
      ? "Needs follow-up"
      : status === "followed_up"
      ? "Followed up"
      : "Follow-up";

  const Icon =
    status === "needs"
      ? AlertCircle
      : status === "followed_up"
      ? CheckCircle
      : ChevronDown;

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold border transition-colors"
        style={pillStyle}
      >
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
        {status === "none" ? null : <ChevronDown className="w-3 h-3 opacity-70" />}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-72 z-50 p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg"
        >
          <div className="rounded-[10px] bg-white p-3">
            <div className="text-[11px] uppercase tracking-wide font-bold text-gray-500 mb-2">
              Follow-up status
            </div>
            <div className="flex flex-col gap-1.5">
              <PopoverOption
                active={status === "none"}
                onClick={() => {
                  setFollowUp("none");
                  setOpen(false);
                }}
                label="No follow-up needed"
              />
              <PopoverOption
                active={status === "needs"}
                onClick={() => setFollowUp("needs", reasonDraft || null)}
                label="Needs follow-up"
                accent="#FF4DB8"
              />
              {status === "needs" || reasonDraft ? (
                <div className="pl-2">
                  <textarea
                    value={reasonDraft}
                    onChange={(e) => setReasonDraft(e.target.value)}
                    onBlur={() => {
                      if (status === "needs") setFollowUp("needs", reasonDraft || null);
                    }}
                    placeholder="What's the follow-up for? (optional)"
                    rows={2}
                    className="w-full text-xs px-2 py-1.5 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-genie-primary/30 resize-none"
                  />
                </div>
              ) : null}
              <PopoverOption
                active={status === "followed_up"}
                onClick={() => {
                  setFollowUp("followed_up");
                  setOpen(false);
                }}
                label="Followed up"
                accent="#4E9CFF"
              />
              {status === "followed_up" && annotations.followedUpAt && (
                <div className="text-[11px] text-gray-500 pl-2 mt-1">
                  Closed{" "}
                  {new Date(annotations.followedUpAt).toLocaleString("en-NZ", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PopoverOption({
  active,
  onClick,
  label,
  accent = "#4E9CFF",
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  accent?: string;
}) {
  return (
    <button
      onClick={onClick}
      className="flex items-center justify-between px-2.5 py-1.5 rounded-md text-sm hover:bg-gray-50 transition-colors text-left"
    >
      <span className={active ? "font-semibold text-gray-900" : "text-gray-700"}>{label}</span>
      {active && <Check className="w-3.5 h-3.5" style={{ color: accent }} />}
    </button>
  );
}
